var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { OGDialog } from '@opengear/modals';
import { OGTable } from '@opengear/table';
import { OGButton } from '@opengear/buttons/button';
import Tooltip from '@mui/material/Tooltip';
import format from 'string-format';
import { OGSubscriptionIcon } from '@opengear/icons/action/subscription-assignment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { formatDateTimestamp } from '@/utils/datetime';
import { NodeStatus, nodeStatusToQuery, subscriptionTypeToQuery, useJson } from '@/utils/query';
import { IQueryNodeField } from '../../../../../types/query/types';
import { useNodes } from '@/api/nodes';
import { FreeTextSearch, useFreeTextSearch, useNodeTagsFieldName } from '@/components/free-text-search';
import { NodeFilter, useNodeFilter } from '@/containers/filters/advanced-filters/node-filter';
import { usePaginationModel } from '@/hooks/pagination-model';
import { useSortModel } from '@/hooks/sort-model';
import { subscriptionTierMap } from '@/utils/license';
import { BulkOperations } from '@/components/nodes/bulk-operations';
import { useUIPermissions } from '@/api/sessions/self/ui';
import { SubscriptionAssignmentForm, SUBSCRIPTION_ASSIGNMENT_MODAL_TITLE_ID } from './subscriptions-assignment-form/subscription-assignment-form';
import { ViewNetopsModules } from './subscriptions-components/view-netops-modules';
import { useDeviceView } from '@/hooks/use-device-view';
// This table is mostly a copy of Pending nodes table
// Table constants
var statusQuery = nodeStatusToQuery(NodeStatus.Enrolled);
var statusFieldNames = [IQueryNodeField.name, IQueryNodeField.id, IQueryNodeField.model, IQueryNodeField.serialNumber];
function SubscriptionsAssignmentTable() {
    var _this = this;
    var t = useTranslation().t;
    var isMobileView = useDeviceView().isMobileView;
    var _a = useUIPermissions(), hasPermission = _a.hasPermission, hasSomePermission = _a.hasSomePermission, isPrimary = _a.isPrimary, isLoadingPermissions = _a.isLoadingPermissions;
    var permissions = useMemo(function () { return ({
        hasNetopsViewAccess: hasPermission('configure.netops.modules.view'),
        hasTemplatePush: hasPermission('configure.configuration_templating.apply_templates.push'),
        hasUpdateSubscription: hasPermission('settings.system.licensing.edit'),
        hasEditableNodeFilters: hasPermission('configure.edit_smart_groups.edit'),
        hasReadableNodeTags: hasSomePermission(['configure.edit_tags.view', 'configure.node_enrollment.enrollment_bundles.edit']),
        hasEditNodesPermission: hasPermission('configure.node_enrollment.enrolled_nodes.enroll'),
        isPrimary: isPrimary,
        isLicExpired: hasPermission('is_lic_expired'),
        isLoadingPermissions: isLoadingPermissions,
    }); }, [hasPermission, hasSomePermission, isPrimary, isLoadingPermissions]);
    // Autocomplete options with default values
    var options = useMemo(function () { return [
        { value: 'all', name: t('pages.subscriptions.tabs.subscription_assignment.filter_by_subscription.options.all') },
        { value: 'unassigned', name: t('pages.subscriptions.tabs.subscription_assignment.filter_by_subscription.options.unassigned') },
        { value: 'enterprise', name: t('pages.subscriptions.tabs.subscription_assignment.filter_by_subscription.options.enterprise') },
        { value: 'premium', name: t('pages.subscriptions.tabs.subscription_assignment.filter_by_subscription.options.premium') },
    ]; }, [t]);
    // Table controls - Server side available query params
    var _b = __read(usePaginationModel('nodes_enrolled'), 3), paginationModel = _b[0], handlePaginationModelChange = _b[1], pageSizeOptions = _b[2];
    var _c = __read(useSortModel('last_updated'), 3), defaultSortModel = _c[0], sortQueryParams = _c[1], handleSortModelChange = _c[2];
    var _d = __read(useState([]), 2), rowSelectionModel = _d[0], setRowSelectionModel = _d[1];
    var refocusRef = useRef(null);
    var _e = __read(useState(), 2), nodeSelection = _e[0], setNodeSelection = _e[1];
    // Node filter
    var _f = useNodeFilter(), nodeFilter = _f.nodeFilter, setNodeFilter = _f.setNodeFilter;
    // adding tag fields to fieldnames if user has access to view tags
    var _g = useNodeTagsFieldName(permissions.hasReadableNodeTags, statusFieldNames), freeTextDisabled = _g.freeTextDisabled, newFieldNames = _g.newFieldNames;
    var _h = __read(useState('all'), 2), subscriptionTypeFilterValue = _h[0], setSubscriptionTypeFilterValue = _h[1];
    var subscriptionTypeQuery = useMemo(function () {
        if (subscriptionTypeFilterValue === 'all')
            return null;
        if (subscriptionTypeFilterValue === 'unassigned')
            return subscriptionTypeToQuery('');
        return subscriptionTypeToQuery(subscriptionTypeFilterValue);
    }, [subscriptionTypeFilterValue]);
    var handleSubscriptionTypeFilterChange = useCallback(function (event) {
        var eventValue = event.target.value;
        setSubscriptionTypeFilterValue(eventValue);
    }, []);
    // Free text search
    var _j = useFreeTextSearch(newFieldNames), textQueryObject = _j.textQueryObject, dispatchTextQuery = _j.dispatchTextQuery;
    // TODO - CDM-183 - extend logic
    // Aggregate all query items into one json query string
    var json = useJson(statusQuery, subscriptionTypeQuery, nodeFilter === null || nodeFilter === void 0 ? void 0 : nodeFilter.queryObject, textQueryObject);
    // Query options
    var queryParams = useMemo(function () { return (__assign({ page: paginationModel.page + 1, per_page: paginationModel.pageSize, json: json }, sortQueryParams)); }, [paginationModel, json, sortQueryParams]);
    // Get Nodes Fetch
    var _k = useNodes(queryParams), data = _k.data, isLoading = _k.isLoading;
    // Derived table values
    var _l = useMemo(function () { return ({
        loading: isLoading || isLoadingPermissions,
        rowCount: data === null || data === void 0 ? void 0 : data.meta.total_count,
        rows: (data === null || data === void 0 ? void 0 : data.nodes) || [],
    }); }, [data === null || data === void 0 ? void 0 : data.meta.total_count, data === null || data === void 0 ? void 0 : data.nodes, isLoading, isLoadingPermissions]), loading = _l.loading, rowCount = _l.rowCount, rows = _l.rows;
    var handleSubscriptionsModalClose = useCallback(function (_a) {
        var clearSelection = _a.clearSelection;
        setNodeSelection(undefined);
        if (clearSelection) {
            setRowSelectionModel([]);
        }
    }, [setNodeSelection, setRowSelectionModel]);
    // bulk nodes subscription assignment handler
    var handleBulkUpdateSubscriptions = useCallback(function (nodeIds) { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setNodeSelection(nodeIds);
                    // Return promise for BulkOperations onClickUpdateSubscriptions type
                    return [4 /*yield*/, Promise.resolve(true)];
                case 1:
                    // Return promise for BulkOperations onClickUpdateSubscriptions type
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, []);
    var renderNodeName = useCallback(function (params) { return params.value; }, []);
    // Column value getters
    var getLastAssignedValue = function (_a) {
        var _b;
        var row = _a.row;
        return (_b = row.subscription) === null || _b === void 0 ? void 0 : _b.last_updated;
    };
    // Column value formatter
    var getLastAssignedFormat = useCallback(function (_a) {
        var value = _a.value;
        return value ? formatDateTimestamp(value) : t('pages.subscriptions.tabs.subscription_assignment.table.unknown');
    }, [t]);
    // TODO CDM-810 Handle click event
    var renderSubscriptionCell = useCallback(function (_a) {
        var row = _a.row;
        return (_jsx(Tooltip, __assign({ arrow: true, placement: "top", title: t('pages.subscriptions.tabs.subscription_assignment.table.actions.assign_subscription.tooltip') }, { children: _jsx("div", { children: _jsx(OGButton, { "aria-label": format(t('pages.subscriptions.tabs.subscription_assignment.table.actions.assign_subscription.label'), row.id), size: "small", startIcon: _jsx(OGSubscriptionIcon, { fontSize: "small" }), variant: "borderless", disabled: !permissions.hasEditNodesPermission || !permissions.hasUpdateSubscription || !permissions.isPrimary || permissions.isLicExpired, onClick: function () { return setNodeSelection([row.id]); }, propagateSpacebar: false }) }) })));
    }, [permissions.hasEditNodesPermission, permissions.hasUpdateSubscription, permissions.isLicExpired, permissions.isPrimary, t]);
    // Column grid
    var columns = useMemo(function () { return [
        { field: 'id', headerName: t('pages.subscriptions.tabs.subscription_assignment.table.headers.id'), minWidth: 110 },
        { field: 'name', headerName: t('pages.subscriptions.tabs.subscription_assignment.table.headers.name'), minWidth: 160, renderCell: renderNodeName },
        {
            field: 'model',
            headerName: t('pages.subscriptions.tabs.subscription_assignment.table.headers.model'),
            valueFormatter: function (_a) {
                var value = _a.value;
                return value !== null && value !== void 0 ? value : t('pages.subscriptions.tabs.subscription_assignment.table.unknown');
            },
            flex: 0.5,
            minWidth: 120,
        },
        {
            field: 'subscription',
            headerName: t('pages.subscriptions.tabs.subscription_assignment.table.headers.subscription_type'),
            valueGetter: function (_a) {
                var _b;
                var row = _a.row;
                return ((_b = row.subscription) === null || _b === void 0 ? void 0 : _b.tier) !== undefined ? subscriptionTierMap.get(row.subscription.tier) : t('global.subscriptions.unassigned');
            },
            flex: 1,
            minWidth: 250,
        },
        {
            field: 'node_netops_modules',
            headerName: t('pages.subscriptions.tabs.subscription_assignment.table.headers.netops'),
            flex: 0.5,
            minWidth: 120,
            renderCell: function (_a) {
                var row = _a.row, tabIndex = _a.tabIndex;
                return (_jsx(ViewNetopsModules, { row: row, tabIndex: tabIndex, hasValidPermissions: permissions.hasNetopsViewAccess && permissions.hasTemplatePush && permissions.isPrimary && !permissions.isLicExpired }));
            },
        },
        {
            field: 'subscription_last_updated',
            headerName: t('pages.subscriptions.tabs.subscription_assignment.table.headers.assigned'),
            valueGetter: getLastAssignedValue,
            valueFormatter: getLastAssignedFormat,
            flex: 1,
            minWidth: 210,
        },
        {
            field: 'update_subscription',
            headerClassName: 'FocusDisabled',
            align: 'center',
            maxWidth: 1,
            sortable: false,
            renderCell: renderSubscriptionCell,
        },
    ]; }, [getLastAssignedFormat, permissions, renderNodeName, renderSubscriptionCell, t]);
    return (_jsxs(_Fragment, { children: [_jsx(OGDialog, __assign({ "aria-labelledby": SUBSCRIPTION_ASSIGNMENT_MODAL_TITLE_ID, open: !!nodeSelection, onClose: handleSubscriptionsModalClose }, { children: nodeSelection && _jsx(SubscriptionAssignmentForm, { onClose: handleSubscriptionsModalClose, nodes: rows, nodeIds: nodeSelection }) })), _jsxs(Stack, { children: [_jsxs(Stack, __assign({ direction: "row", gap: 1, marginBottom: 1, justifyContent: "space-between", flexWrap: "wrap" }, { children: [_jsxs(Stack, __assign({ direction: "row", gap: 1, flexWrap: "wrap" }, { children: [_jsx(Box, __assign({ marginRight: 1 }, { children: _jsx(NodeFilter, { filter: nodeFilter, setFilter: setNodeFilter, permissions: { hasSmartgroupsEditAccess: permissions.hasEditableNodeFilters } }) })), _jsx(Select, __assign({ onChange: handleSubscriptionTypeFilterChange, sx: { width: isMobileView ? '100%' : '250px', margin: 0 }, inputProps: { 'aria-label': t('pages.subscriptions.tabs.subscription_assignment.filter_by_subscription.label') }, value: subscriptionTypeFilterValue }, { children: options.map(function (option) { return (_jsx(MenuItem, __assign({ value: option.value }, { children: option.name }), option.name)); }) }))] })), _jsx(FreeTextSearch, { ref: refocusRef, onSearch: dispatchTextQuery, freeTextDisabled: freeTextDisabled, useDebounce: false, translations: { tooltip: t('components.nodes.text_search.tooltip') } })] })), permissions.hasEditNodesPermission && permissions.hasUpdateSubscription && !permissions.isLicExpired && (_jsx(BulkOperations, { nodeIds: rowSelectionModel, totalNodes: rows.length, setRowSelectionModel: setRowSelectionModel, refocusRef: refocusRef, onClickUpdateSubscriptions: handleBulkUpdateSubscriptions })), _jsx(Box, { children: _jsx(OGTable, { id: "subscriptions-nodes-table", "aria-label": t('pages.subscriptions.tabs.subscription_assignment.table.label'), autoHeight: true, columns: columns, rows: rows, loading: loading, noRowsMessage: t('pages.subscriptions.tabs.subscription_assignment.table.no_row_message.enrolled'), pageSizeOptions: pageSizeOptions, pagination: true, paginationMode: "server", paginationModel: paginationModel, onPaginationModelChange: handlePaginationModelChange, rowCount: rowCount, sortingMode: "server", onSortModelChange: handleSortModelChange, checkboxSelection: permissions.hasEditNodesPermission && permissions.hasUpdateSubscription && !permissions.isLicExpired, onRowSelectionModelChange: setRowSelectionModel, rowSelectionModel: rowSelectionModel, initialState: {
                                sorting: {
                                    sortModel: defaultSortModel,
                                },
                            } }) })] })] }));
}
// Reduce re-renders
export default memo(SubscriptionsAssignmentTable);
